import { useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useIkalStore } from '@/store/ikal.store';
import { useAuthStore } from '@/store/auth.store';

// Igual que axios y el socket: en desarrollo con Docker el proxy de Vite no alcanza al backend,
// así que se llama directo a VITE_API_URL (vacío en producción, donde nginx sirve /api).
const API_BASE = import.meta.env.VITE_API_URL ?? '';

export function useIkalStream() {
  const {
    conversacionId,
    contexto,
    addUserMessage,
    setConversacionId,
    startStream,
    appendDelta,
    commitStream,
    setPendingProposal,
    addErrorMessage,
  } = useIkalStore();

  const usuario = useAuthStore((s) => s.usuario);
  const abortRef = useRef<AbortController | null>(null);

  const send = useCallback(
    async (mensaje: string) => {
      if (!mensaje.trim() || !usuario) return;

      addUserMessage(mensaje);
      startStream();
      setPendingProposal(null);

      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      try {
        const body = JSON.stringify({
          mensaje,
          conversacionId: conversacionId ?? undefined,
          contexto: contexto ?? undefined,
        });

        const res = await fetch(`${API_BASE}/api/ikal/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body,
          signal: ctrl.signal,
        });

        if (!res.ok) {
          let errMsg = `Error ${res.status}`;
          try {
            const errBody = await res.json();
            errMsg = errBody?.message ?? errBody?.error ?? errMsg;
            if (Array.isArray(errBody?.message)) errMsg = errBody.message.join(', ');
          } catch { /* non-json body */ }
          console.error('[Ikal] HTTP error:', res.status, errMsg);
          addErrorMessage(`Error al conectar con Ikal (${res.status}): ${errMsg}`);
          commitStream();
          return;
        }

        if (!res.body) {
          addErrorMessage('Ikal no devolvió respuesta.');
          commitStream();
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });

          const lines = buf.split('\n');
          buf = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data:')) continue;
            const raw = line.slice(5).trim();
            if (!raw) continue;

            try {
              const event = JSON.parse(raw);
              if (event.type === 'text') {
                appendDelta(event.delta ?? '');
              } else if (event.type === 'tool_proposal') {
                commitStream();
                setPendingProposal({
                  conversacionId: event.conversacionId,
                  tool: event.tool,
                  toolId: event.toolId,
                  input: event.input,
                  humanReadable: event.humanReadable,
                });
                setConversacionId(event.conversacionId);
              } else if (event.type === 'done') {
                setConversacionId(event.conversacionId);
                commitStream();
              } else if (event.type === 'error') {
                addErrorMessage(event.message ?? 'Error interno de Ikal');
                commitStream();
              }
            } catch { /* ignore parse errors */ }
          }
        }

        commitStream();
      } catch (e: any) {
        if (e?.name === 'AbortError') {
          commitStream();
          return;
        }
        console.error('[Ikal] fetch error:', e);
        toast.error('No se pudo conectar con Ikal. Verifica tu conexión.');
        commitStream();
      }
    },
    [conversacionId, contexto, usuario, addUserMessage, startStream, appendDelta, commitStream, setPendingProposal, setConversacionId, addErrorMessage],
  );

  const confirmar = useCallback(
    async (confirmed: boolean) => {
      const store = useIkalStore.getState();
      const proposal = store.pendingProposal;
      if (!proposal) return;

      store.setPendingProposal(null);
      if (confirmed) store.startStream();
      else {
        store.appendDelta('Entendido, he cancelado la acción.');
        store.commitStream();
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/api/ikal/confirmar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ conversacionId: proposal.conversacionId, confirmed }),
        });

        if (!res.ok) {
          let errMsg = `Error ${res.status}`;
          try { const b = await res.json(); errMsg = b?.message ?? errMsg; } catch { /* */ }
          store.addErrorMessage(`Error al confirmar acción (${res.status}): ${errMsg}`);
          commitStream();
          return;
        }

        if (!res.body) { commitStream(); return; }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop() ?? '';
          for (const line of lines) {
            if (!line.startsWith('data:')) continue;
            const raw = line.slice(5).trim();
            if (!raw) continue;
            try {
              const event = JSON.parse(raw);
              if (event.type === 'text') store.appendDelta(event.delta ?? '');
              else if (event.type === 'done') { setConversacionId(event.conversacionId); store.commitStream(); }
              else if (event.type === 'error') { store.addErrorMessage(event.message ?? 'Error'); store.commitStream(); }
              else if (event.type === 'tool_proposal') {
                store.commitStream();
                store.setPendingProposal({
                  conversacionId: event.conversacionId,
                  tool: event.tool,
                  toolId: event.toolId,
                  input: event.input,
                  humanReadable: event.humanReadable,
                });
              }
            } catch { /* ignore */ }
          }
        }
        commitStream();
      } catch { commitStream(); }
    },
    [commitStream, setConversacionId],
  );

  return { send, confirmar };
}
